import { useState, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useOrgPermissions } from '@/hooks/useOrgPermissions'
import { useToast } from '@/hooks/use-toast'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ExternalLink, BarChart2, Globe, FileText, Star, Plus, Check, ChevronsUpDown, Loader2 } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'
import { cn } from '@/lib/utils'
import { logActivity } from '@/lib/activityLog'

const PAGE_SIZE = 25

const DAYS_FOR_RANGE: Record<string, number | null> = {
  '30d': 30, '90d': 90, '6m': 180, '1y': 365, 'all': null,
}

function fmtReach(n: number | null): string {
  if (!n) return ''
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`
  return String(n)
}

function scoreColor(score: number): string {
  if (score >= 80) return '#006B3C'
  if (score >= 60) return '#BA7517'
  return '#A32D2D'
}

const SCORE_COMPONENTS = [
  { key: 'score_volume',        label: 'Volume',           max: 150 },
  { key: 'score_reach',         label: 'Reach',            max: 250 },
  { key: 'score_engagement',    label: 'Engagement',       max: 200 },
  { key: 'score_breadth',       label: 'Breadth',          max: 150 },
  { key: 'score_recency',       label: 'Recency',          max: 100 },
  { key: 'score_format_div',    label: 'Format Diversity', max: 50  },
  { key: 'score_accreditation', label: 'Accreditation',    max: 100 },
]

export function CoverageAnalyticsView() {
  const { orgId, can } = useOrgPermissions()
  const { toast } = useToast()
  const qc = useQueryClient()

  const [selectedJournalist, setSelectedJournalist] = useState<any>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [overrideForm, setOverrideForm] = useState({
    score_volume: 0, score_accuracy: 0, score_tone: 0,
    score_reach: 0, score_regularity: 0, override_notes: '',
  })
  const [savingOverride, setSavingOverride] = useState(false)
  const [addArticleOpen, setAddArticleOpen] = useState(false)
  const [jPopoverOpen, setJPopoverOpen] = useState(false)
  const [submittingArticle, setSubmittingArticle] = useState(false)
  const [recomputing, setRecomputing] = useState(false)
  const [articleForm, setArticleForm] = useState({
    journalist_id: '', url: '', headline: '',
    published_at: new Date().toISOString().split('T')[0],
    beat: '', reach_estimate: '',
  })
  const [page, setPage] = useState(0)
  const [dateRange, setDateRange] = useState<'30d'|'90d'|'6m'|'1y'|'all'>('90d')
  const [selectedJournalistId, setSelectedJournalistId] = useState<string|null>(null)

  const { data: journalists } = useQuery({
    queryKey: ['coverage-journalists', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await supabase
        .from('journalists')
        .select(`
          id, first_name, last_name, nationality, beats, seniority,
          journalist_affiliations(is_primary, outlet_id, media_outlets(name))
        `)
        .eq('org_id', orgId!)
        .eq('status', 'active')
        .order('last_name')
      return (data || []) as any[]
    }
  })

  const { data: articles } = useQuery({
    queryKey: ['coverage-articles', orgId, page, dateRange],
    enabled: !!orgId,
    queryFn: async () => {
      const days = DAYS_FOR_RANGE[dateRange]
      const since = days !== null
        ? new Date(Date.now() - days * 86400000).toISOString()
        : null

      let q = (supabase
        .from('coverage_articles')
        .select(
          `id, journalist_id, headline, url, published_at, submitted_at, beat,
           reach_estimate, total_score, ai_scored, staff_override`,
          { count: 'exact' }
        )
        .eq('org_id', orgId!)
        .order('submitted_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1) as any)

      if (since) q = q.gte('submitted_at', since)

      const { data, count, error } = await q
      if (error) throw error
      return { rows: (data || []) as any[], total: count || 0 }
    }
  })

  const { data: journalistArticles } = useQuery({
    queryKey: ['journalist-articles-sheet', selectedJournalist?.id, orgId],
    enabled: !!selectedJournalist?.id && !!orgId && sheetOpen,
    queryFn: async () => {
      const { data } = await supabase
        .from('coverage_articles')
        .select('*')
        .eq('journalist_id', selectedJournalist.id)
        .eq('org_id', orgId!)
        .order('published_at', { ascending: false })
      return data || []
    }
  })

  const { data: scoreBreakdown } = useQuery({
    queryKey: ['journalist-score-breakdown', selectedJournalistId],
    enabled: !!selectedJournalistId,
    queryFn: async () => {
      const { data } = await (supabase
        .from('journalist_coverage_scores' as any)
        .select('*')
        .eq('journalist_id', selectedJournalistId!)
        .maybeSingle() as any)
      return data
    }
  })

  const articleRows = articles?.rows ?? []
  const articleTotal = articles?.total ?? 0

  const weeklyData = useMemo(() => {
    if (!articleRows.length) return []
    const byWeek: Record<string, number> = {}
    articleRows.forEach((a: any) => {
      const dateStr = a.submitted_at || a.published_at
      if (!dateStr) return
      const d = new Date(dateStr)
      const weekStart = new Date(d)
      weekStart.setDate(d.getDate() - d.getDay())
      const key = weekStart.toISOString().split('T')[0]
      byWeek[key] = (byWeek[key] || 0) + 1
    })
    return Object.entries(byWeek)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([week, count]) => ({ week, count }))
  }, [articleRows])

  const maxWeeklyCount = weeklyData.length ? Math.max(...weeklyData.map(w => w.count), 1) : 1

  if (!can('coverage_analytics', 'view')) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
        You do not have access to Coverage Analytics.
      </div>
    )
  }

  const journalistScoreMap: Record<string, { count: number; total: number }> = {}
  articleRows.forEach((a: any) => {
    if (!journalistScoreMap[a.journalist_id]) {
      journalistScoreMap[a.journalist_id] = { count: 0, total: 0 }
    }
    journalistScoreMap[a.journalist_id].count += 1
    journalistScoreMap[a.journalist_id].total += a.total_score || 0
  })

  const leaderboard = (journalists || [])
    .map((j: any) => {
      const stats = journalistScoreMap[j.id] || { count: 0, total: 0 }
      const avgScore = stats.count > 0 ? Math.round(stats.total / stats.count) : 0
      const primaryAffil = (j.journalist_affiliations || []).find((a: any) => a.is_primary)
      return { ...j, avgScore, articleCount: stats.count, primaryOutlet: primaryAffil?.media_outlets?.name }
    })
    .sort((a: any, b: any) => b.avgScore - a.avgScore)

  const topScore = leaderboard[0]?.avgScore ?? 0
  const totalAvgScore = leaderboard.length
    ? Math.round(leaderboard.reduce((s: number, j: any) => s + j.avgScore, 0) / leaderboard.length)
    : 0
  const uniqueCountries = new Set(leaderboard.map((j: any) => j.nationality).filter(Boolean)).size

  const beatCounts: Record<string, number> = {}
  articleRows.forEach((a: any) => {
    const b = a.beat || 'Unclassified'
    beatCounts[b] = (beatCounts[b] || 0) + 1
  })
  const maxBeatCount = Math.max(...Object.values(beatCounts), 1)
  const beatEntries = Object.entries(beatCounts).sort((a, b) => b[1] - a[1]).slice(0, 10)

  const selectedJournalistName = selectedJournalistId
    ? (journalists || []).find((j: any) => j.id === selectedJournalistId)
    : null

  const openSheet = (journalist: any) => {
    setSelectedJournalist(journalist)
    setOverrideForm({ score_volume: 0, score_accuracy: 0, score_tone: 0, score_reach: 0, score_regularity: 0, override_notes: '' })
    setSheetOpen(true)
  }

  const saveOverride = async (articleId: string) => {
    setSavingOverride(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      await supabase
        .from('coverage_articles')
        .update({
          score_volume:     overrideForm.score_volume,
          score_accuracy:   overrideForm.score_accuracy,
          score_tone:       overrideForm.score_tone,
          score_reach:      overrideForm.score_reach,
          score_regularity: overrideForm.score_regularity,
          override_notes:   overrideForm.override_notes,
          staff_override:   true,
          reviewed_by:      user?.id,
        })
        .eq('id', articleId)
      toast({ title: 'Score updated.' })
      qc.invalidateQueries({ queryKey: ['coverage-articles', orgId] })
      qc.invalidateQueries({ queryKey: ['journalist-articles-sheet', selectedJournalist?.id, orgId] })
    } catch {
      toast({ title: 'Failed to save override.', variant: 'destructive' })
    } finally {
      setSavingOverride(false)
    }
  }

  const selectedArticleJournalist = (journalists || []).find((j: any) => j.id === articleForm.journalist_id)
  const selectedArticleOutlet = selectedArticleJournalist
    ? (selectedArticleJournalist.journalist_affiliations || []).find((a: any) => a.is_primary)?.media_outlets?.name
    : undefined

  const handleRecompute = async () => {
    if (!orgId) return
    setRecomputing(true)
    try {
      const unscored = articleRows.filter((a: any) => !a.ai_scored)
      if (unscored.length === 0) {
        toast({ title: 'All articles already scored.' })
        return
      }
      await Promise.allSettled(
        unscored.map((a: any) =>
          supabase.functions.invoke('score-coverage-article', {
            body: { article_id: a.id, headline: a.headline, url: a.url, beat: a.beat, reach_estimate: a.reach_estimate },
          })
        )
      )
      toast({ title: `Recomputed scores for ${unscored.length} article${unscored.length === 1 ? '' : 's'}.` })
      qc.invalidateQueries({ queryKey: ['coverage-articles', orgId] })
    } catch {
      toast({ title: 'Recompute failed.', variant: 'destructive' })
    } finally {
      setRecomputing(false)
    }
  }

  const openAddArticle = () => {
    setArticleForm({
      journalist_id: '', url: '', headline: '',
      published_at: new Date().toISOString().split('T')[0],
      beat: '', reach_estimate: '',
    })
    setAddArticleOpen(true)
  }

  const submitArticle = async () => {
    if (!articleForm.journalist_id || !articleForm.url.trim() || !articleForm.headline.trim() || !orgId) return
    setSubmittingArticle(true)
    try {
      const { data: inserted, error } = await supabase
        .from('coverage_articles')
        .insert({
          org_id: orgId,
          journalist_id: articleForm.journalist_id,
          url: articleForm.url.trim(),
          headline: articleForm.headline.trim(),
          published_at: articleForm.published_at,
          beat: articleForm.beat || null,
          reach_estimate: articleForm.reach_estimate ? parseInt(articleForm.reach_estimate) : null,
        })
        .select('id')
        .single()
      if (error) throw error

      const { data: scoreData } = await supabase.functions.invoke('score-coverage-article', {
        body: {
          article_id: inserted.id,
          headline: articleForm.headline.trim(),
          url: articleForm.url.trim(),
          beat: articleForm.beat,
          reach_estimate: articleForm.reach_estimate ? parseInt(articleForm.reach_estimate) : null,
        },
      })

      const totalScore = scoreData
        ? (scoreData.score_volume ?? 0) + (scoreData.score_accuracy ?? 0) + (scoreData.score_tone ?? 0) + (scoreData.score_reach ?? 0) + (scoreData.score_regularity ?? 0)
        : null

      await logActivity({
        orgId,
        actionType: 'article_submitted',
        description: `Article submitted: ${articleForm.headline.trim()}`,
        metadata: { article_id: inserted.id, journalist_id: articleForm.journalist_id },
      })

      toast({ title: totalScore != null ? `AI scored: ${totalScore}/100` : 'Article added.' })
      setAddArticleOpen(false)
      qc.invalidateQueries({ queryKey: ['coverage-articles', orgId] })
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    } finally {
      setSubmittingArticle(false)
    }
  }

  const RANGE_LABELS: Record<string, string> = {
    '30d': '30 days', '90d': '90 days', '6m': '6 months', '1y': '1 year', 'all': 'All time',
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div />
        {can('coverage_analytics', 'edit') && (
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" onClick={handleRecompute} disabled={recomputing}>
              {recomputing
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <BarChart2 className="w-4 h-4 mr-2" />}
              {recomputing ? 'Recomputing…' : 'Recompute Unscored'}
            </Button>
            <Button onClick={openAddArticle}>
              <Plus className="w-4 h-4 mr-2" />
              Add Article
            </Button>
          </div>
        )}
      </div>

      {/* Date range filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Period</span>
        {(['30d', '90d', '6m', '1y', 'all'] as const).map(d => (
          <button
            key={d}
            onClick={() => { setDateRange(d); setPage(0) }}
            className={`px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors ${
              dateRange === d
                ? 'bg-[#0D2240] text-white'
                : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            {RANGE_LABELS[d]}
          </button>
        ))}
        <span className="ml-auto text-[11px] text-muted-foreground">
          {articleTotal} article{articleTotal !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Total Articles</span>
            </div>
            <div className="text-2xl font-bold">{articleTotal}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <BarChart2 className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Avg Coverage Score</span>
            </div>
            <div className="text-2xl font-bold" style={{ color: scoreColor(totalAvgScore) }}>
              {totalAvgScore}<span className="text-sm font-normal text-muted-foreground">/100</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Star className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Top Score</span>
            </div>
            <div className="text-2xl font-bold" style={{ color: scoreColor(topScore) }}>
              {topScore}<span className="text-sm font-normal text-muted-foreground">/100</span>
            </div>
            {leaderboard[0] && (
              <div className="text-xs text-muted-foreground mt-1 truncate">
                {leaderboard[0].first_name} {leaderboard[0].last_name}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Countries Reached</span>
            </div>
            <div className="text-2xl font-bold">{uniqueCountries}</div>
          </CardContent>
        </Card>
      </div>

      {/* Trend chart */}
      {weeklyData.length > 1 && (
        <Card>
          <CardContent className="p-4">
            <div className="text-[12px] font-medium text-muted-foreground uppercase tracking-wide mb-4">
              Articles per week — trend
            </div>
            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={weeklyData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="week"
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 6,
                    border: '1px solid hsl(var(--border))',
                    background: 'hsl(var(--card))',
                    color: 'hsl(var(--foreground))',
                  }}
                  formatter={(value: number) => [value, 'Articles']}
                  labelFormatter={(label) => `Week of ${label}`}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#006B3C"
                  strokeWidth={2}
                  dot={{ fill: '#006B3C', r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Score breakdown panel */}
      {scoreBreakdown && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[13px] font-semibold">
                Score Breakdown
                {selectedJournalistName && (
                  <span className="text-muted-foreground font-normal ml-1">
                   , {selectedJournalistName.first_name} {selectedJournalistName.last_name}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-[#C8A84B] bg-[#C8A84B]/10 px-2 py-0.5 rounded-full">
                  {(scoreBreakdown.badge_tier || 'none').toUpperCase()}
                </span>
                <button
                  onClick={() => setSelectedJournalistId(null)}
                  className="text-muted-foreground hover:text-foreground text-sm leading-none"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              {SCORE_COMPONENTS.map(({ key, label, max }) => {
                const value = scoreBreakdown[key] || 0
                const fill = max > 0 ? Math.min((value / max) * 100, 100) : 0
                return (
                  <div key={key} className="mb-2">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-[12px] text-muted-foreground">{label}</span>
                      <span className="text-[11px] font-medium tabular-nums">{Math.round(value)}/{max}</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#006B3C] rounded-full transition-all"
                        style={{ width: `${fill}%` }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
              <span className="text-[12px] text-muted-foreground">Total Score</span>
              <span className="text-[15px] font-bold text-[#0D2240] dark:text-white">
                {Math.round(scoreBreakdown.score_total || 0)} / 1000
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leaderboard + Beat panel */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">
                Journalist Leaderboard
                <span className="text-[11px] font-normal text-muted-foreground ml-2">
                  Click row for score breakdown
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground w-8">#</th>
                    <th className="px-2 py-2.5 text-left font-medium text-muted-foreground">Journalist</th>
                    <th className="px-2 py-2.5 text-left font-medium text-muted-foreground hidden md:table-cell">Outlet</th>
                    <th className="px-2 py-2.5 text-left font-medium text-muted-foreground hidden lg:table-cell">Beat</th>
                    <th className="px-2 py-2.5 text-left font-medium text-muted-foreground w-28">Score</th>
                    <th className="px-2 py-2.5 text-center font-medium text-muted-foreground w-16">Art.</th>
                    <th className="px-2 py-2.5 w-20" />
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground text-sm">
                        No coverage data yet. Articles submitted via the journalist portal will appear here.
                      </td>
                    </tr>
                  ) : leaderboard.map((j: any, idx: number) => (
                    <tr
                      key={j.id}
                      className={`border-b last:border-0 hover:bg-muted/20 cursor-pointer ${
                        j.id === selectedJournalistId ? 'bg-muted/30' : ''
                      }`}
                      onClick={() => setSelectedJournalistId(j.id === selectedJournalistId ? null : j.id)}
                    >
                      <td className="px-4 py-3">
                        <span className={`text-sm font-bold ${idx < 3 ? 'text-[#C8A84B]' : 'text-muted-foreground'}`}>
                          {idx + 1}
                        </span>
                      </td>
                      <td className="px-2 py-3">
                        <div className="font-medium">{j.first_name} {j.last_name}</div>
                        {j.nationality && (
                          <div className="text-[11px] text-muted-foreground">{j.nationality}</div>
                        )}
                      </td>
                      <td className="px-2 py-3 text-muted-foreground hidden md:table-cell text-sm">
                        {j.primaryOutlet || ''}
                      </td>
                      <td className="px-2 py-3 hidden lg:table-cell">
                        {j.beats?.[0] ? (
                          <Badge variant="outline" className="text-[10px]">{j.beats[0]}</Badge>
                        ) : ''}
                      </td>
                      <td className="px-2 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden w-16">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${j.avgScore}%`,
                                backgroundColor: scoreColor(j.avgScore),
                              }}
                            />
                          </div>
                          <span className="text-xs font-medium tabular-nums" style={{ color: scoreColor(j.avgScore) }}>
                            {j.avgScore}
                          </span>
                        </div>
                      </td>
                      <td className="px-2 py-3 text-center text-muted-foreground text-sm">
                        {j.articleCount}
                      </td>
                      <td className="px-2 py-3">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs"
                          onClick={(e) => { e.stopPropagation(); openSheet(j) }}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-2">
          <Card className="h-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">Coverage by Beat</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {beatEntries.length === 0 ? (
                <p className="text-justify text-sm text-muted-foreground">No articles yet.</p>
              ) : beatEntries.map(([beat, count]) => (
                <div key={beat} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-28 truncate shrink-0">{beat}</span>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[#C8A84B]"
                      style={{ width: `${Math.round((count / maxBeatCount) * 100)}%` }}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground w-6 text-right">{count}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recent Articles table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Recent Articles</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Headline</th>
                <th className="px-2 py-2.5 text-left font-medium text-muted-foreground hidden md:table-cell">Journalist</th>
                <th className="px-2 py-2.5 text-left font-medium text-muted-foreground hidden lg:table-cell">Beat</th>
                <th className="px-2 py-2.5 text-left font-medium text-muted-foreground w-24">Published</th>
                <th className="px-2 py-2.5 text-right font-medium text-muted-foreground w-16">Reach</th>
                <th className="px-2 py-2.5 text-center font-medium text-muted-foreground w-20">Score</th>
                <th className="px-2 py-2.5 text-center font-medium text-muted-foreground w-20">Status</th>
              </tr>
            </thead>
            <tbody>
              {articleRows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                    No articles submitted yet.
                  </td>
                </tr>
              ) : articleRows.map((a: any) => {
                const j = (journalists || []).find((x: any) => x.id === a.journalist_id)
                return (
                  <tr key={a.id} className="border-b last:border-0 hover:bg-muted/10">
                    <td className="px-4 py-3 max-w-xs">
                      <a
                        href={a.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium hover:underline flex items-center gap-1 truncate"
                      >
                        {(a.headline || '').slice(0, 60)}{a.headline?.length > 60 ? '…' : ''}
                        <ExternalLink className="w-3 h-3 shrink-0 text-muted-foreground" />
                      </a>
                    </td>
                    <td className="px-2 py-3 text-muted-foreground hidden md:table-cell">
                      {j ? `${j.first_name} ${j.last_name}` : ''}
                    </td>
                    <td className="px-2 py-3 hidden lg:table-cell">
                      {a.beat ? <Badge variant="outline" className="text-[10px]">{a.beat}</Badge> : ''}
                    </td>
                    <td className="px-2 py-3 text-muted-foreground whitespace-nowrap">
                      {a.published_at ? new Date(a.published_at).toLocaleDateString() : ''}
                    </td>
                    <td className="px-2 py-3 text-right text-muted-foreground">
                      {fmtReach(a.reach_estimate)}
                    </td>
                    <td className="px-2 py-3 text-center">
                      {a.total_score != null ? (
                        <span className="text-xs font-bold" style={{ color: scoreColor(a.total_score) }}>
                          {a.total_score}
                        </span>
                      ) : ''}
                    </td>
                    <td className="px-2 py-3 text-center">
                      {a.staff_override ? (
                        <Badge className="text-[10px] bg-amber-100 text-amber-700">Reviewed</Badge>
                      ) : a.ai_scored ? (
                        <Badge className="text-[10px] bg-green-100 text-green-700">AI Scored</Badge>
                      ) : ''}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          {/* Pagination */}
          {articleTotal > PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <span className="text-[12px] text-muted-foreground">
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, articleTotal)} of {articleTotal}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                >
                  ← Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  disabled={(page + 1) * PAGE_SIZE >= articleTotal}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next →
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Journalist article detail sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="w-[520px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {selectedJournalist ? `${selectedJournalist.first_name} ${selectedJournalist.last_name}` : ''}
            </SheetTitle>
            {selectedJournalist?.primaryOutlet && (
              <p className="text-justify text-sm text-muted-foreground">{selectedJournalist.primaryOutlet}</p>
            )}
          </SheetHeader>

          <div className="mt-4 space-y-4">
            {(journalistArticles || []).length === 0 ? (
              <p className="text-justify text-sm text-muted-foreground">No articles on record for this journalist.</p>
            ) : (journalistArticles || []).map((a: any) => (
              <div key={a.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <a
                      href={a.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium hover:underline flex items-center gap-1"
                    >
                      {a.headline}
                      <ExternalLink className="w-3 h-3 shrink-0 text-muted-foreground" />
                    </a>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {a.beat && <Badge variant="outline" className="text-[10px]">{a.beat}</Badge>}
                      {a.published_at && (
                        <span className="text-[11px] text-muted-foreground">
                          {new Date(a.published_at).toLocaleDateString()}
                        </span>
                      )}
                      {a.total_score != null && (
                        <span className="text-[11px] font-bold" style={{ color: scoreColor(a.total_score) }}>
                          Score: {a.total_score}
                        </span>
                      )}
                      {a.staff_override && (
                        <Badge className="text-[10px] bg-amber-100 text-amber-700">Reviewed</Badge>
                      )}
                      {a.ai_scored && !a.staff_override && (
                        <Badge className="text-[10px] bg-green-100 text-green-700">AI Scored</Badge>
                      )}
                    </div>
                  </div>
                </div>

                {can('coverage_analytics', 'edit') && (
                  <details className="group">
                    <summary className="text-xs text-[#C8A84B] cursor-pointer hover:underline">
                      Override Score
                    </summary>
                    <div className="mt-3 space-y-3 border-t pt-3">
                      <div className="grid grid-cols-2 gap-3">
                        {(['score_volume','score_accuracy','score_tone','score_reach','score_regularity'] as const).map(field => (
                          <div key={field} className="space-y-1">
                            <Label className="text-[11px]">
                              {field.replace('score_', '').replace('_', ' ')
                                .replace(/\b\w/g, c => c.toUpperCase())}
                              <span className="text-muted-foreground"> (0-20)</span>
                            </Label>
                            <Input
                              type="number"
                              min={0}
                              max={20}
                              value={overrideForm[field]}
                              onChange={e => setOverrideForm(f => ({ ...f, [field]: parseInt(e.target.value) || 0 }))}
                              className="h-7 text-xs"
                            />
                          </div>
                        ))}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px]">Override Notes</Label>
                        <Textarea
                          rows={2}
                          value={overrideForm.override_notes}
                          onChange={e => setOverrideForm(f => ({ ...f, override_notes: e.target.value }))}
                          placeholder="Reason for manual override…"
                          className="text-xs resize-none"
                        />
                      </div>
                      <Button
                        size="sm"
                        className="text-xs bg-[#C8A84B] hover:bg-[#B8983B] text-white"
                        disabled={savingOverride}
                        onClick={() => saveOverride(a.id)}
                      >
                        {savingOverride ? 'Saving…' : 'Save Override'}
                      </Button>
                    </div>
                  </details>
                )}
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {/* Add Article sheet */}
      <Sheet open={addArticleOpen} onOpenChange={setAddArticleOpen}>
        <SheetContent side="right" className="w-[520px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Add Coverage Article</SheetTitle>
          </SheetHeader>

          <div className="mt-5 space-y-4">
            <div className="space-y-1.5">
              <Label>Journalist</Label>
              <Popover open={jPopoverOpen} onOpenChange={setJPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
                    {selectedArticleJournalist
                      ? `${selectedArticleJournalist.first_name} ${selectedArticleJournalist.last_name}`
                      : 'Select journalist…'}
                    <ChevronsUpDown className="ml-2 w-4 h-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[460px] p-0">
                  <Command>
                    <CommandInput placeholder="Search journalist…" />
                    <CommandList>
                      <CommandEmpty>No journalist found.</CommandEmpty>
                      <CommandGroup>
                        {(journalists || []).map((j: any) => (
                          <CommandItem
                            key={j.id}
                            value={`${j.first_name} ${j.last_name}`}
                            onSelect={() => {
                              setArticleForm(f => ({ ...f, journalist_id: j.id, beat: j.beats?.[0] || f.beat }))
                              setJPopoverOpen(false)
                            }}
                          >
                            <Check className={cn('mr-2 w-4 h-4', articleForm.journalist_id === j.id ? 'opacity-100' : 'opacity-0')} />
                            {j.first_name} {j.last_name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedArticleOutlet && (
                <p className="text-justify text-xs text-muted-foreground">Outlet: {selectedArticleOutlet}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="article-url">Published URL</Label>
              <Input
                id="article-url"
                value={articleForm.url}
                onChange={e => setArticleForm(f => ({ ...f, url: e.target.value }))}
                placeholder="https://…"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="article-headline">Headline</Label>
              <Input
                id="article-headline"
                value={articleForm.headline}
                onChange={e => setArticleForm(f => ({ ...f, headline: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="article-date">Published Date</Label>
                <Input
                  id="article-date"
                  type="date"
                  value={articleForm.published_at}
                  onChange={e => setArticleForm(f => ({ ...f, published_at: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="article-beat">Beat</Label>
                <Input
                  id="article-beat"
                  value={articleForm.beat}
                  onChange={e => setArticleForm(f => ({ ...f, beat: e.target.value }))}
                  placeholder="e.g. Trade"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="article-reach">Reach Estimate (optional)</Label>
              <Input
                id="article-reach"
                type="number"
                min={0}
                value={articleForm.reach_estimate}
                onChange={e => setArticleForm(f => ({ ...f, reach_estimate: e.target.value }))}
              />
            </div>

            <Button
              className="w-full bg-[#C8A84B] hover:bg-[#B8983B] text-white"
              disabled={submittingArticle || !articleForm.journalist_id || !articleForm.url.trim() || !articleForm.headline.trim()}
              onClick={submitArticle}
            >
              {submittingArticle && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Submit Article
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
